import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  EditIcon,
  HomeIcon,
  BuildingIcon,
  BriefcaseIcon,
  ClipboardListIcon,
  FileTextIcon,
  DollarSignIcon,
  PlusIcon,
  UsersIcon,
  AlertTriangleIcon,
  StickyNoteIcon,
  HistoryIcon,
  ClipboardIcon,
  MoreHorizontalIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { PropertyQrPointsPanel } from '../components/property/PropertyQrPointsPanel'
import { ConversationsPanel } from '../components/ConversationsPanel'
import { getEntityActivities, ActivityLog, logActivity } from '../utils/activityLogger'
import { parseEntityNotes } from '../lib/entityNotes'
import { NotesAndActivityPanel } from '../components/NotesAndActivityPanel'
import { getContactDisplayName } from '../lib/leadHelpers'
import {
  formatPropertyPaymentMethod,
  formatPropertySalesTaxStatus,
} from '../components/property/types'
import CityRecordsTab from '../components/CityRecordsTab'
import { MobilePage, MobilePageHeader, MobileSheet, mobile } from '../components/mobile'
import { ViewTabBar } from '../components/ViewPageLayout'
import { usePermissions } from '../hooks/usePermissions'
import { usesFieldHome } from '../lib/fieldHome'
import { PropertyJobChatsPanel } from '../components/property/PropertyJobChatsPanel'

interface PropertyNote {
  timestamp: string
  note: string
}

export default function PropertyView() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const { profile, isSuperAdmin, loading: permLoading } = usePermissions()
  const fieldHome = usesFieldHome(profile?.role, isSuperAdmin)
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'contacts'
    | 'jobs'
    | 'job_chats'
    | 'contracts'
    | 'proposals'
    | 'invoices'
    | 'notes'
    | 'city_records'
    | 'qr'
  >('overview')

  const [property, setProperty] = useState<any>(null)
  const [customer, setCustomer] = useState<any>(null)
  const [customerContacts, setCustomerContacts] = useState<any[]>([])
  const [propertyContacts, setPropertyContacts] = useState<any[]>([])
  const [notes, setNotes] = useState<PropertyNote[]>([])
  const [activities, setActivities] = useState<ActivityLog[]>([])
  const [jobs, setJobs] = useState<any[]>([])
  const [contracts, setContracts] = useState<any[]>([])
  const [proposals, setProposals] = useState<any[]>([])
  const [proposalRequests, setProposalRequests] = useState<any[]>([])
  const [invoices, setInvoices] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)

  useEffect(() => {
    if (!id || permLoading || fieldHome) return
    fetchPropertyData()
    fetchActivities()
  }, [id, permLoading, fieldHome])

  useEffect(() => {
    if (activeTab === 'notes' && id) {
      fetchActivities()
    }
  }, [activeTab, id])

  const fetchPropertyData = async () => {
    try {
      // Fetch property
      const { data: propertyData, error: propertyError } = await supabase
        .from('properties')
        .select('*')
        .eq('id', id)
        .single()

      if (propertyError) throw propertyError

      let enriched = propertyData
      if (propertyData.default_assigned_crew_id || propertyData.default_assigned_employee_id) {
        const [crewRes, empRes] = await Promise.all([
          propertyData.default_assigned_crew_id
            ? supabase
                .from('crews')
                .select('id, name')
                .eq('id', propertyData.default_assigned_crew_id)
                .maybeSingle()
            : Promise.resolve({ data: null }),
          propertyData.default_assigned_employee_id
            ? supabase
                .from('employees')
                .select('id, first_name, last_name')
                .eq('id', propertyData.default_assigned_employee_id)
                .maybeSingle()
            : Promise.resolve({ data: null }),
        ])
        enriched = {
          ...propertyData,
          default_crew: crewRes.data,
          default_employee: empRes.data,
        }
      }
      setProperty(enriched)

      // Fetch customer
      if (propertyData.customer_id) {
        const { data: customerData, error: customerError } = await supabase
          .from('customers')
          .select('*')
          .eq('id', propertyData.customer_id)
          .single()

        if (!customerError && customerData) {
          setCustomer(customerData)
          // Fetch customer contacts
          if (customerData.contacts && Array.isArray(customerData.contacts)) {
            setCustomerContacts(customerData.contacts)
          }
        }
      }

      // Fetch property contacts if available
      if (propertyData.contacts && Array.isArray(propertyData.contacts)) {
        setPropertyContacts(propertyData.contacts)
      } else {
        // If property doesn't have contacts, use customer contacts as default
        if (customer && customer.contacts && Array.isArray(customer.contacts)) {
          setPropertyContacts(customer.contacts)
        }
      }

      // Fetch notes if available
      setNotes(parseEntityNotes(propertyData.notes))

      // Fetch related records
      await Promise.all([
        fetchJobs(propertyData.id),
        fetchContracts(propertyData.id),
        fetchProposals(propertyData.id),
        fetchProposalRequests(propertyData.id),
        fetchInvoices(propertyData.id),
      ])
    } catch (error) {
      console.error('Error fetching property data:', error)
      alert('Error loading property: ' + (error as any).message)
    } finally {
      setLoading(false)
    }
  }

  const fetchActivities = async () => {
    try {
      const acts = await getEntityActivities('property', id!)
      setActivities(acts)
    } catch (error) {
      console.error('Error fetching activities:', error)
    }
  }

  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return

    setSavingNote(true)
    try {
      const noteToAdd: PropertyNote = {
        timestamp: new Date().toISOString(),
        note: newNote.trim(),
      }

      const updatedNotes = [...notes, noteToAdd]

      const { error } = await supabase
        .from('properties')
        .update({ notes: updatedNotes })
        .eq('id', id)

      if (error) throw error

      setNotes(updatedNotes)
      setNewNote('')

      await logActivity({
        activity_type: 'note_added',
        entity_type: 'property',
        entity_id: id,
        description: 'Property note added',
      })
      fetchActivities()
    } catch (error) {
      console.error('Error adding note:', error)
      alert('Error adding note: ' + (error as any).message)
    } finally {
      setSavingNote(false)
    }
  }

  const fetchJobs = async (propertyId: string) => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: false })

      if (!error && data) {
        setJobs(data)
      }
    } catch (error) {
      console.error('Error fetching jobs:', error)
    }
  }

  const fetchContracts = async (propertyId: string) => {
    try {
      const [legacyResult, scopedResult] = await Promise.all([
        supabase
          .from('contracts')
          .select('*')
          .eq('property_id', propertyId)
          .order('created_at', { ascending: false }),
        supabase
          .from('contract_properties')
          .select('contract:contracts(*)')
          .eq('property_id', propertyId)
          .eq('status', 'active'),
      ])
      if (legacyResult.error) throw legacyResult.error
      const scopedContracts = (scopedResult.data || [])
        .map((row: any) => (Array.isArray(row.contract) ? row.contract[0] : row.contract))
        .filter(Boolean)
      const byId = new Map<string, any>()
      for (const contract of [...(legacyResult.data || []), ...scopedContracts]) {
        byId.set(contract.id, contract)
      }
      setContracts(
        Array.from(byId.values()).sort((a, b) =>
          String(b.created_at || '').localeCompare(String(a.created_at || ''))
        )
      )
    } catch (error) {
      console.error('Error fetching contracts:', error)
    }
  }

  const fetchProposals = async (propertyId: string) => {
    try {
      // First try to fetch by property_id (only actual proposals, not requests)
      const { data, error } = await supabase
        .from('proposals')
        .select('*')
        .eq('property_id', propertyId)
        .eq('record_type', 'proposal')
        .order('created_at', { ascending: false })

      if (!error && data && data.length > 0) {
        setProposals(data)
        return
      }

      // If no proposals found by property_id, also check by customer_id
      // This handles cases where property_id wasn't properly linked during conversion
      if (property?.customer_id) {
        const { data: customerProposals, error: customerError } = await supabase
          .from('proposals')
          .select('*')
          .eq('customer_id', property.customer_id)
          .eq('record_type', 'proposal')
          .order('created_at', { ascending: false })

        if (!customerError && customerProposals) {
          // Filter to show proposals that might match this property by address
          const matchingProposals = customerProposals.filter(p =>
            p.property_id === propertyId ||
            p.project_address?.includes(property.address?.split(',')[0]) ||
            !p.property_id // Include proposals without property_id assigned
          )
          setProposals(matchingProposals)
        }
      }
    } catch (error) {
      console.error('Error fetching proposals:', error)
    }
  }

  const fetchProposalRequests = async (propertyId: string) => {
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select('*')
        .eq('property_id', propertyId)
        .eq('record_type', 'request')
        .order('created_at', { ascending: false })

      if (error) throw error
      setProposalRequests(data || [])
    } catch (error) {
      console.error('Error fetching proposal requests:', error)
    }
  }


  const fetchInvoices = async (propertyId: string) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: false })

      if (!error && data) {
        setInvoices(data)
      }
    } catch (error) {
      console.error('Error fetching invoices:', error)
    }
  }

  if (!permLoading && fieldHome && id) {
    const qs = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''
    return <Navigate to={`/my-properties/${id}${qs}`} replace />
  }

  if (loading) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg text-gray-600">Loading...</div>
      </MobilePage>
    )
  }

  if (!property) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg text-gray-600">Property not found</div>
      </MobilePage>
    )
  }

  const handleBack = () => {
    if (returnTo) {
      navigate(decodeURIComponent(returnTo))
    } else {
      navigate('/properties')
    }
  }
  const backLabel = returnTo
    ? (() => {
        const decoded = decodeURIComponent(returnTo)
        if (decoded.includes('/proposals/view')) return 'Back to Proposal'
        if (decoded.includes('/contracts/view') || decoded.includes('/contracts/details'))
          return 'Back to Contract'
        if (decoded.includes('/jobs/details')) return 'Back to Job'
        if (decoded.includes('/invoices/')) return 'Back to Invoice'
        if (decoded.includes('/customers/view')) return 'Back to Customer'
        return 'Back'
      })()
    : 'Back to Properties'

  const addressLine = [
    property.address || 'No address provided',
    property.address_line_2,
    [property.city, property.state].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title={property.name || 'Property'}
        subtitle={
          <span className="flex items-start gap-2">
            <HomeIcon className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <span>{addressLine}</span>
          </span>
        }
        onBack={handleBack}
        backLabel={backLabel}
        actions={
          <button
            type="button"
            onClick={() => setActionsOpen(true)}
            className={`${mobile.secondaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm`}
            aria-label="More actions"
          >
            <MoreHorizontalIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Actions</span>
          </button>
        }
      />

      <div className={`${mobile.surface} flex flex-col min-h-0`}>
        <ViewTabBar
          sticky
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'contacts', label: 'Contacts' },
            { id: 'jobs', label: 'Jobs' },
            { id: 'job_chats', label: 'Chats', full: 'Job chats' },
            { id: 'contracts', label: 'Contracts' },
            { id: 'proposals', label: 'Proposals' },
            { id: 'invoices', label: 'Invoices' },
            { id: 'qr', label: 'QR', full: 'QR Points' },
            { id: 'notes', label: 'Notes', full: 'Notes & Activity' },
            {
              id: 'city_records',
              label: 'City',
              full: 'City Records',
              accent: true,
              icon: <AlertTriangleIcon className="w-4 h-4" />,
            },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

          <div className="p-3 sm:p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <BriefcaseIcon className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-gray-900">Jobs</h3>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{jobs.length}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ClipboardListIcon className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-gray-900">Contracts</h3>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{contracts.length}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileTextIcon className="w-5 h-5 text-orange-600" />
                      <h3 className="font-semibold text-gray-900">Proposals</h3>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{proposals.length}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSignIcon className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-gray-900">Invoices</h3>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{invoices.length}</p>
                  </div>
                </div>

                {id && <ConversationsPanel entityType="property" entityId={id} />}

                {/* Property Information */}
                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <BuildingIcon className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Property Information</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Property Name
                      </label>
                      <p className="mt-1 text-sm text-gray-900">{property.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Building Type
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {property.building_type || 'N/A'}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {property.address || 'N/A'}
                        {property.address_line_2 && (
                          <>
                            <br />
                            {property.address_line_2}
                          </>
                        )}
                        {property.city && `, ${property.city}`}
                        {property.state && `, ${property.state}`}
                        {property.zip_code && ` ${property.zip_code}`}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Units</label>
                      <p className="mt-1 text-sm text-gray-900">{property.units || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Stories</label>
                      <p className="mt-1 text-sm text-gray-900">{property.stories || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Access Type</label>
                      <p className="mt-1 text-sm text-gray-900">{property.access_type || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Access Info</label>
                      <p className="mt-1 text-sm text-gray-900">{property.access_info || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Payment Method
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatPropertyPaymentMethod(property.payment_method, customer)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Sales Tax Status
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatPropertySalesTaxStatus(property.sales_tax_status, customer)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Default Crew
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {property.default_crew?.name || 'Unassigned'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Default Employee
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {property.default_employee
                          ? `${property.default_employee.first_name || ''} ${property.default_employee.last_name || ''}`.trim()
                          : 'Unassigned'}
                      </p>
                    </div>
                  </div>
                </div>

                {customer && (
                  <div className="bg-teal-50 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <BuildingIcon className="w-6 h-6 text-teal-600" />
                      <h2 className="text-xl font-semibold text-gray-900">Customer</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                        <label className="block text-sm font-medium text-gray-700">Company</label>
                        <p className="mt-1 text-sm font-medium text-gray-900">
                          {customer.company_name || 'N/A'}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <p className="mt-1 text-sm text-gray-900">{customer.email || 'N/A'}</p>
                      </div>
                      <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                        <p className="mt-1 text-sm text-gray-900">{customer.phone || 'N/A'}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/customers/view/${customer.id}`)}
                      className="mt-4 text-sm text-teal-700 hover:text-teal-900 font-medium"
                    >
                      View Customer →
                    </button>
                  </div>
                )}

                {/* Additional Access */}
                {property.additional_access &&
                  Array.isArray(property.additional_access) &&
                  property.additional_access.length > 0 && (
                    <div className="bg-green-50 rounded-lg p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <BuildingIcon className="w-6 h-6 text-green-600" />
                        <h2 className="text-xl font-semibold text-gray-900">Additional Access</h2>
                      </div>
                      <div className="space-y-3">
                        {property.additional_access.map((access: any, index: number) => (
                          <div key={index} className="bg-white rounded-lg border border-green-100 p-4 shadow-sm">
                            <p className="text-sm font-medium text-gray-900">
                              {access.location || 'Unknown Location'}
                            </p>
                            <p className="text-sm text-gray-600">
                              {access.access_type} - {access.access_info}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div className="space-y-6">
                <div className="bg-green-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <UsersIcon className="w-6 h-6 text-green-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Contacts ({propertyContacts.length})
                    </h2>
                  </div>
                  {propertyContacts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {propertyContacts.map((contact, index) => (
                        <div
                          key={contact.id || index}
                          className="bg-white rounded-lg border border-green-100 p-4 shadow-sm"
                        >
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {getContactDisplayName(contact)}
                          </h3>
                          {contact.title && (
                            <p className="text-sm text-gray-600 mb-3">{contact.title}</p>
                          )}
                          <div className="space-y-2 text-sm">
                            {contact.phone && (
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Phone</label>
                                <a
                                  href={`tel:${contact.phone.replace(/\D/g, '')}`}
                                  className="text-blue-600 hover:text-blue-800 underline"
                                >
                                  {contact.phone}
                                </a>
                              </div>
                            )}
                            {contact.cell && (
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Cell</label>
                                <a
                                  href={`tel:${contact.cell.replace(/\D/g, '')}`}
                                  className="text-blue-600 hover:text-blue-800 underline"
                                >
                                  {contact.cell}
                                </a>
                              </div>
                            )}
                            {contact.email && (
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Email</label>
                                <a
                                  href={`mailto:${contact.email}`}
                                  className="text-blue-600 hover:text-blue-800 underline break-all"
                                >
                                  {contact.email}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-dashed border-green-200 p-8 text-center">
                      <UsersIcon className="w-10 h-10 text-green-300 mx-auto mb-3" />
                      <p className="text-gray-500">No contacts found</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Jobs Tab */}
            {activeTab === 'jobs' && (
              <div className="space-y-6">
                <div className="bg-sky-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <BriefcaseIcon className="w-6 h-6 text-sky-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Jobs ({jobs.length})</h2>
                  </div>
                  {jobs.length === 0 ? (
                    <div className="bg-white rounded-lg border border-dashed border-sky-200 p-8 text-center">
                      <BriefcaseIcon className="w-10 h-10 text-sky-300 mx-auto mb-3" />
                      <p className="text-gray-500">No jobs found for this property</p>
                    </div>
                  ) : (
                    <>
                    <ul className="space-y-2 lg:hidden">
                      {jobs.map((job) => (
                        <li key={job.id} className="rounded-xl border border-sky-100 bg-white p-3 space-y-1.5">
                          <button
                            type="button"
                            onClick={() => navigate(`/jobs/details/${job.id}`)}
                            className="text-sm font-semibold text-blue-600 text-left"
                          >
                            {job.title || 'Untitled'}
                          </button>
                          <p className="text-xs text-gray-500">{job.job_number || 'N/A'}</p>
                          <p className="text-sm text-gray-900">{job.status || 'N/A'}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-sky-100 shadow-sm">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-sky-50/80">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job #</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {jobs.map((job) => (
                            <tr key={job.id} className="hover:bg-sky-50/30">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">{job.job_number || 'N/A'}</td>
                              <td className="px-6 py-4 text-sm text-gray-900">{job.title || 'Untitled'}</td>
                              <td className="px-6 py-4 text-sm text-gray-600">{job.status || 'N/A'}</td>
                              <td className="px-6 py-4 text-sm">
                                <button
                                  onClick={() => navigate(`/jobs/details/${job.id}`)}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Job chats Tab */}
            {activeTab === 'job_chats' && id && (
              <div className="space-y-6">
                <PropertyJobChatsPanel propertyId={id} />
              </div>
            )}

            {/* Contracts Tab */}
            {activeTab === 'contracts' && (
              <div className="space-y-6">
                <div className="bg-emerald-50 rounded-lg p-6">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <ClipboardIcon className="w-6 h-6 text-emerald-600" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        Contracts ({contracts.length})
                      </h2>
                    </div>
                    <button
                    onClick={() => {
                      const returnTo = encodeURIComponent(`/properties/view/${id}?tab=contracts`)
                      navigate(`/contracts/create?propertyId=${id}&returnTo=${returnTo}`)
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <PlusIcon className="w-4 h-4" />
                    New Contract
                  </button>
                </div>

                {contracts.length === 0 ? (
                  <div className="bg-white rounded-lg border border-dashed border-emerald-200 p-8 text-center">
                    <ClipboardIcon className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
                    <p className="text-gray-500">No contracts found for this property</p>
                  </div>
                ) : (
                  <>
                  <ul className="space-y-2 lg:hidden">
                    {contracts.map((contract) => (
                      <li key={contract.id} className="rounded-xl border border-emerald-100 bg-white p-3 space-y-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            const returnTo = encodeURIComponent(`/properties/view/${id}?tab=contracts`)
                            navigate(`/contracts/details/${contract.id}?returnTo=${returnTo}`)
                          }}
                          className="text-sm font-semibold text-blue-600 text-left"
                        >
                          {contract.title || 'Untitled Contract'}
                        </button>
                        <p className="text-xs text-gray-500">{contract.contract_number || 'N/A'}</p>
                        <p className="text-sm text-gray-900">
                          ${(contract.total_amount || 0).toFixed(2)} · {contract.status || 'draft'}
                        </p>
                      </li>
                    ))}
                  </ul>
                  <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-emerald-100 shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-emerald-50/80">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contract Number
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Title
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {contracts.map((contract) => (
                          <tr key={contract.id} className="hover:bg-emerald-50/30">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {contract.contract_number || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.title || 'Untitled Contract'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {contract.contract_type === 'recurring'
                                ? 'Recurring'
                                : contract.contract_type === 'as_needed'
                                  ? 'Ongoing (As Needed)'
                                  : 'One-Time'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  contract.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : contract.status === 'draft'
                                    ? 'bg-gray-100 text-gray-800'
                                    : contract.status === 'pending_signature'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : contract.status === 'cancelled'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {contract.status || 'draft'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ${(contract.total_amount || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => {
                                  const returnTo = encodeURIComponent(`/properties/view/${id}?tab=contracts`)
                                  navigate(`/contracts/details/${contract.id}?returnTo=${returnTo}`)
                                }}
                                className="text-blue-600 hover:text-blue-900 mr-4"
                              >
                                View
                              </button>
                              <button
                                onClick={() => {
                                  const returnTo = encodeURIComponent(`/properties/view/${id}?tab=contracts`)
                                  navigate(`/contracts/edit/${contract.id}?returnTo=${returnTo}`)
                                }}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
                </div>
              </div>
            )}

            {/* Proposals Tab */}
            {activeTab === 'proposals' && (
              <div className="space-y-6">
                <div className="bg-orange-50 rounded-lg p-6">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <FileTextIcon className="w-6 h-6 text-orange-600" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        Proposals ({proposals.length})
                      </h2>
                    </div>
                    <button
                      onClick={() => {
                        const returnTo = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
                        navigate(`/proposals/create?property=${id}&returnTo=${returnTo}`)
                      }}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      <PlusIcon className="w-4 h-4" />
                      New Proposal
                    </button>
                  </div>

                {proposals.length === 0 ? (
                  <div className="bg-white rounded-lg border border-dashed border-orange-200 p-8 text-center">
                    <FileTextIcon className="w-10 h-10 text-orange-300 mx-auto mb-3" />
                    <p className="text-gray-500">No proposals found for this property</p>
                  </div>
                ) : (
                  <>
                  <ul className="space-y-2 lg:hidden">
                    {proposals.map((proposal) => (
                      <li key={proposal.id} className="rounded-xl border border-orange-100 bg-white p-3 space-y-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            const returnTo = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
                            navigate(`/proposals/view/${proposal.id}?returnTo=${returnTo}`)
                          }}
                          className="text-sm font-semibold text-blue-600 text-left"
                        >
                          {proposal.title || 'Untitled'}
                        </button>
                        <p className="text-xs text-gray-500">{proposal.proposal_number}</p>
                        <p className="text-sm text-gray-900">
                          ${(proposal.total || proposal.total_amount || 0).toLocaleString()} · {proposal.status || 'draft'}
                        </p>
                      </li>
                    ))}
                  </ul>
                  <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-orange-100 shadow-sm">
                    <table className="w-full">
                      <thead className="bg-orange-50/80">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Title
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {proposals.map((proposal) => (
                          <tr key={proposal.id} className="hover:bg-orange-50/30">
                            <td className="px-4 py-3">
                              <button
                                onClick={() => {
                                  const returnTo = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
                                  navigate(`/proposals/view/${proposal.id}?returnTo=${returnTo}`)
                                }}
                                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {proposal.title || 'Untitled'}
                              </button>
                              <div className="text-xs text-gray-500">{proposal.proposal_number}</div>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              ${(proposal.total || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  proposal.status === 'approved'
                                    ? 'bg-green-100 text-green-800'
                                    : proposal.status === 'sent'
                                    ? 'bg-blue-100 text-blue-800'
                                    : proposal.status === 'rejected'
                                    ? 'bg-red-100 text-red-800'
                                    : proposal.status === 'expired'
                                    ? 'bg-gray-100 text-gray-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {proposal.status?.charAt(0).toUpperCase() + proposal.status?.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(proposal.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    const returnTo = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
                                    navigate(`/proposals/view/${proposal.id}?returnTo=${returnTo}`)
                                  }}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  View
                                </button>
                                <button
                                  onClick={() => {
                                    const returnTo = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
                                    navigate(`/proposals/edit/${proposal.id}?returnTo=${returnTo}`)
                                  }}
                                  className="text-gray-600 hover:text-gray-800 text-sm"
                                >
                                  Edit
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
                </div>

                {/* Proposal Requests Section */}
                <div className="bg-indigo-50 rounded-lg p-6">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <ClipboardListIcon className="w-6 h-6 text-indigo-600" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        Proposal Requests ({proposalRequests.length})
                      </h2>
                    </div>
                    <button
                      onClick={() => {
                        const returnTo = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
                        navigate(`/proposal-requests/create?propertyId=${id}&customerId=${property?.customer_id || ''}&returnTo=${returnTo}`)
                      }}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      <PlusIcon className="w-4 h-4" />
                      New Request
                    </button>
                  </div>

                  {proposalRequests.length === 0 ? (
                    <div className="bg-white rounded-lg border border-dashed border-indigo-200 p-8 text-center">
                      <ClipboardListIcon className="w-10 h-10 text-indigo-300 mx-auto mb-3" />
                      <p className="text-gray-500">No proposal requests found</p>
                    </div>
                  ) : (
                    <>
                    <ul className="space-y-2 lg:hidden">
                      {proposalRequests.map((request) => (
                        <li key={request.id} className="rounded-xl border border-indigo-100 bg-white p-3 space-y-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              const returnTo = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
                              navigate(`/proposal-requests/view/${request.id}?returnTo=${returnTo}`)
                            }}
                            className="text-sm font-semibold text-blue-600 text-left"
                          >
                            {request.title || 'Untitled Request'}
                          </button>
                          <p className="text-xs text-gray-500">{request.request_number || request.proposal_number}</p>
                          <p className="text-sm text-gray-900">{request.status || 'submitted'}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-indigo-100 shadow-sm">
                      <table className="w-full">
                        <thead className="bg-indigo-50/80">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Title
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Amount
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {proposalRequests.map((request) => (
                            <tr key={request.id} className="hover:bg-indigo-50/30">
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => {
                                    const returnTo = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
                                    navigate(`/proposal-requests/view/${request.id}?returnTo=${returnTo}`)
                                  }}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {request.title || 'Untitled Request'}
                                </button>
                                <div className="text-xs text-gray-500">{request.request_number || request.proposal_number}</div>
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {request.estimated_budget ? `$${request.estimated_budget.toLocaleString()}` : 'N/A'}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    request.status === 'converted'
                                      ? 'bg-green-100 text-green-800'
                                      : request.status === 'submitted'
                                      ? 'bg-blue-100 text-blue-800'
                                      : request.status === 'reviewing' || request.status === 'in_progress'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : request.status === 'declined'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {request.status?.charAt(0).toUpperCase() + request.status?.slice(1) || 'Submitted'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {new Date(request.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      const returnTo = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
                                      navigate(`/proposal-requests/view/${request.id}?returnTo=${returnTo}`)
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    View
                                  </button>
                                  <button
                                    onClick={() => {
                                      const returnTo = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
                                      navigate(`/proposal-requests/edit/${request.id}?returnTo=${returnTo}`)
                                    }}
                                    className="text-gray-600 hover:text-gray-800 text-sm"
                                  >
                                    Edit
                                  </button>
                                  {request.status !== 'converted' && (
                                    <button
                                      onClick={() => {
                                        const returnTo = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
                                        navigate(`/proposals/create?requestId=${request.id}&returnTo=${returnTo}`)
                                      }}
                                      className="text-green-600 hover:text-green-800 text-sm"
                                      title="Create Proposal"
                                    >
                                      Create Proposal
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <div className="space-y-6">
                <div className="bg-cyan-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <DollarSignIcon className="w-6 h-6 text-cyan-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Invoices ({invoices.length})
                    </h2>
                  </div>
                  {invoices.length === 0 ? (
                    <div className="bg-white rounded-lg border border-dashed border-cyan-200 p-8 text-center">
                      <DollarSignIcon className="w-10 h-10 text-cyan-300 mx-auto mb-3" />
                      <p className="text-gray-500">No invoices found for this property</p>
                    </div>
                  ) : (
                    <>
                    <ul className="space-y-2 lg:hidden">
                      {invoices.map((invoice) => (
                        <li key={invoice.id} className="rounded-xl border border-cyan-100 bg-white p-3 space-y-1.5">
                          <button
                            type="button"
                            onClick={() => navigate(`/invoice-generation?invoice=${invoice.id}`)}
                            className="text-sm font-semibold text-blue-600 text-left"
                          >
                            {invoice.invoice_number || 'N/A'}
                          </button>
                          <p className="text-sm text-gray-900">
                            ${(invoice.total || invoice.total_amount || 0).toLocaleString()} · {invoice.status || 'N/A'}
                          </p>
                        </li>
                      ))}
                    </ul>
                    <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-cyan-100 shadow-sm">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-cyan-50/80">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {invoices.map((invoice) => (
                            <tr key={invoice.id} className="hover:bg-cyan-50/30">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">{invoice.invoice_number || 'N/A'}</td>
                              <td className="px-6 py-4 text-sm text-gray-900">${(invoice.total_amount || 0).toFixed(2)}</td>
                              <td className="px-6 py-4 text-sm text-gray-600">{invoice.status || 'N/A'}</td>
                              <td className="px-6 py-4 text-sm">
                                <button
                                  onClick={() => navigate(`/invoice-generation?invoice=${invoice.id}`)}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'qr' && id && (
              <PropertyQrPointsPanel propertyId={id} />
            )}

            {activeTab === 'notes' && (
              <NotesAndActivityPanel
                notes={[...notes].reverse().map((n) => ({ note: n.note, timestamp: n.timestamp }))}
                activities={activities}
                notesTitle="Property Notes"
                newNote={newNote}
                onNewNoteChange={setNewNote}
                onAddNote={handleAddNote}
                savingNote={savingNote}
              />
            )}
            {/* City Records Tab */}
            {activeTab === 'city_records' && (
              <CityRecordsTab
                propertyId={property.id}
                customerId={customer?.id ?? null}
              />
            )}
          </div>
      </div>

      <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:mt-4 lg:rounded-xl lg:border lg:border-gray-200">
        <button type="button" onClick={handleBack} className={mobile.secondaryBtn}>
          {backLabel}
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setActionsOpen(true)} className={mobile.secondaryBtn}>
            <MoreHorizontalIcon className="w-4 h-4" />
            Actions
          </button>
          <button
            type="button"
            onClick={() => navigate(`/properties/edit/${property.id}`)}
            className={mobile.primaryBtn}
          >
            <EditIcon className="w-4 h-4" />
            Edit Property
          </button>
        </div>
      </div>

      <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Property actions">
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              const rt = encodeURIComponent(`/properties/view/${id}?tab=contracts`)
              navigate(`/contracts/create?propertyId=${id}&returnTo=${rt}`)
            }}
            className={`${mobile.secondaryBtn} w-full`}
          >
            <ClipboardIcon className="w-4 h-4" />
            New Contract
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              const rt = encodeURIComponent(`/properties/view/${id}?tab=proposals`)
              navigate(`/proposals/create?property=${id}&returnTo=${rt}`)
            }}
            className={`${mobile.secondaryBtn} w-full`}
          >
            <FileTextIcon className="w-4 h-4" />
            New Proposal
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              setActiveTab('city_records')
            }}
            className={`${mobile.secondaryBtn} w-full`}
          >
            <AlertTriangleIcon className="w-4 h-4" />
            City Records
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              navigate(`/properties/edit/${property.id}`)
            }}
            className={`${mobile.primaryBtn} w-full`}
          >
            <EditIcon className="w-4 h-4" />
            Edit Property
          </button>
        </div>
      </MobileSheet>
    </MobilePage>
  )
}
